import { useMemo, useState } from 'react';

import {
  KeyRound,
  Mail,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  UserCheck,
  UserX,
  Users as UsersIcon,
} from 'lucide-react';

import { ApiError } from '@/api/client';
import type { UserRead, UserStatus } from '@/api/schema';
import { Page, PageHeader, Panel, Toolbar } from '@/components/layout/Page';
import { useToast } from '@/components/feedback/Toast';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Chip, type ChipTone } from '@/components/ui/Chip';
import { ConfirmModal } from '@/components/ui/Modal';
import { ErrorState, NoData, NoResults } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { SkeletonTable } from '@/components/ui/Skeleton';
import { StatStrip } from '@/components/ui/StatStrip';
import { Table, TableWrap, Td, Th, Tr, type SortDirection } from '@/components/ui/Table';
import { useCurrentUser } from '@/features/auth/api';
import { NOTHING } from '@/lib/nothing';
import { can } from '@/lib/permissions';
import { useDebounced } from '@/lib/useDebounced';

import {
  photoUrlFor,
  useBulkAction,
  useDepartments,
  useRemoveUser,
  useResendInvite,
  useResetPassword,
  useSetUserStatus,
  useUsers,
  type BulkAction,
  type UserFilters,
} from '../api';
import { InviteLinkModal, type InviteLinkKind } from '../components/InviteLinkModal';
import { UserDrawer } from '../components/UserDrawer';
import { RowMenu } from '../components/RowMenu';
import styles from './UsersPage.module.css';

const PER_PAGE = 15;

const STATUS_TONE: Record<UserStatus, ChipTone> = {
  invited: 'hold',
  active: 'approved',
  deactivated: 'applied',
};

const STATUS_LABEL: Record<UserStatus, string> = {
  invited: 'Invited',
  active: 'Active',
  deactivated: 'Deactivated',
};

/** "2d ago" is all anyone reads a last-active column for. */
function lastActive(value: string | null): string {
  if (!value) return NOTHING;
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}

type IssuedLink = {
  kind: InviteLinkKind;
  name: string;
  token: string;
  expiresAt: string;
};

export function UsersPage() {
  const toast = useToast();
  const { data: currentUser } = useCurrentUser();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<UserStatus | ''>('');
  const [departmentId, setDepartmentId] = useState('');
  const [sortBy, setSortBy] = useState('full_name');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);

  const [drawer, setDrawer] = useState<{ open: boolean; user: UserRead | null }>({
    open: false,
    user: null,
  });
  const [link, setLink] = useState<IssuedLink | null>(null);
  const [confirm, setConfirm] = useState<{ ids: string[]; label: string } | null>(null);

  // Typing should not fire a request per keystroke.
  const debouncedSearch = useDebounced(search, 300);

  const filters: UserFilters = useMemo(
    () => ({
      page,
      per_page: PER_PAGE,
      search: debouncedSearch.trim(),
      status,
      department_id: departmentId,
      sort_by: sortBy,
      sort_dir: sortDir,
    }),
    [page, debouncedSearch, status, departmentId, sortBy, sortDir],
  );

  const users = useUsers(filters);
  const departments = useDepartments();
  const setStatusFor = useSetUserStatus();
  const removeUser = useRemoveUser();
  const bulk = useBulkAction();
  const resendInvite = useResendInvite();
  const resetPassword = useResetPassword();

  const canEdit = can(currentUser, 'settings', 'edit');
  const canCreate = can(currentUser, 'settings', 'create');
  const canDelete = can(currentUser, 'settings', 'delete');

  // Memoised because a fresh [] each render would re-run every memo that reads it.
  const items = useMemo(() => users.data?.items ?? [], [users.data]);
  const total = users.data?.total ?? 0;
  const filtered = Boolean(debouncedSearch.trim() || status || departmentId);

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setDepartmentId('');
    setPage(1);
  };

  const sortOn = (key: string) => {
    if (sortBy === key) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  // Selection is per page: the ids of rows that are not on screen cannot be reasoned
  // about, and a "select all" that silently spans pages is how people delete more
  // than they meant to.
  const pageIds = items.map((user) => user.id);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const someSelected = pageIds.some((id) => selected.includes(id)) && !allSelected;

  const runBulk = (action: BulkAction) => {
    bulk.mutate(
      { action, ids: selected },
      {
        onSuccess: (result) => {
          const verb =
            action === 'remove'
              ? 'removed'
              : action === 'activate'
                ? 'activated'
                : 'deactivated';
          toast.success(`${result.affected} staff ${verb}.`);
          setSelected([]);
        },
        onError: (error) => {
          toast.error(
            error instanceof ApiError
              ? error.message
              : 'That did not work. Nothing was changed.',
          );
        },
        onSettled: () => {
          setConfirm(null);
        },
      },
    );
  };

  const issue = (kind: InviteLinkKind, user: UserRead) => {
    const mutation = kind === 'invite' ? resendInvite : resetPassword;
    mutation.mutate(user.id, {
      onSuccess: (result) => {
        setLink({
          kind,
          name: user.full_name,
          token: result.token,
          expiresAt: result.expires_at,
        });
      },
      onError: (error) => {
        toast.error(error instanceof ApiError ? error.message : 'That did not work.');
      },
    });
  };

  const counts = useMemo(() => {
    // From the loaded page only, so it is honest about what it is counting.
    const byStatus = (value: UserStatus) =>
      items.filter((user) => user.status === value).length;
    return [
      { label: 'Staff', value: total },
      { label: 'Active', value: byStatus('active'), tone: 'success' as const },
      { label: 'Invited', value: byStatus('invited'), tone: 'warning' as const },
      { label: 'Deactivated', value: byStatus('deactivated'), tone: 'faint' as const },
    ];
  }, [items, total]);

  return (
    <Page>
      <PageHeader
        title="User management"
        description="Staff accounts that can sign in to Flyst. Control who has access, what role they hold, and deactivate accounts when people leave."
        actions={
          canCreate ? (
            <Button
              icon={<Plus size={16} />}
              onClick={() => {
                setDrawer({ open: true, user: null });
              }}
            >
              Add staff member
            </Button>
          ) : null
        }
      />

      <StatStrip items={counts} />

      <Panel flush>
        <Toolbar>
          <div className={styles.search}>
            <Input
              icon={<Search size={15} />}
              placeholder="Search name or email"
              aria-label="Search staff"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>

          <Select
            aria-label="Filter by status"
            placeholder="All statuses"
            value={status}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'invited', label: 'Invited' },
              { value: 'deactivated', label: 'Deactivated' },
            ]}
            onChange={(event) => {
              setStatus(event.target.value as UserStatus | '');
              setPage(1);
            }}
          />

          <Select
            aria-label="Filter by department"
            placeholder="All departments"
            value={departmentId}
            options={(departments.data ?? []).map((department) => ({
              value: department.id,
              label: department.name,
            }))}
            onChange={(event) => {
              setDepartmentId(event.target.value);
              setPage(1);
            }}
          />

          {filtered ? (
            <Button size="sm" variant="ghost" onClick={clearFilters}>
              Clear
            </Button>
          ) : null}
        </Toolbar>

        {selected.length > 0 ? (
          <div className={styles.bulkBar}>
            <b>{selected.length} selected</b>
            <div className={styles.bulkActions}>
              {canEdit ? (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<UserCheck size={14} />}
                    loading={bulk.isPending}
                    onClick={() => {
                      runBulk('activate');
                    }}
                  >
                    Activate
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<UserX size={14} />}
                    loading={bulk.isPending}
                    onClick={() => {
                      runBulk('deactivate');
                    }}
                  >
                    Deactivate
                  </Button>
                </>
              ) : null}
              {canDelete ? (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 size={14} />}
                  onClick={() => {
                    setConfirm({
                      ids: selected,
                      label: `${selected.length} staff member${selected.length === 1 ? '' : 's'}`,
                    });
                  }}
                >
                  Remove
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelected([]);
                }}
              >
                Clear selection
              </Button>
            </div>
          </div>
        ) : null}

        {/* The four states (plan 9.4). */}
        {users.isPending ? (
          <SkeletonTable rows={8} columns={6} label="Loading staff" />
        ) : users.isError ? (
          <ErrorState
            message={
              users.error instanceof ApiError
                ? users.error.message
                : 'The staff list did not load.'
            }
            onRetry={() => {
              void users.refetch();
            }}
          />
        ) : items.length === 0 && filtered ? (
          <NoResults onClear={clearFilters} label="staff" />
        ) : items.length === 0 ? (
          <NoData
            icon={<UsersIcon size={21} />}
            title="No staff yet"
            body="Add the first account and send them an invitation."
            {...(canCreate
              ? {
                  action: {
                    label: 'Add staff member',
                    onClick: () => {
                      setDrawer({ open: true, user: null });
                    },
                  },
                }
              : {})}
          />
        ) : (
          <>
            <TableWrap>
              <Table minWidth={900}>
                <thead>
                  <tr>
                    {canEdit ? (
                      <Th label="" className={styles.checkCell}>
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          aria-label="Select everything on this page"
                          onChange={() => {
                            setSelected((current) =>
                              allSelected
                                ? current.filter((id) => !pageIds.includes(id))
                                : [...new Set([...current, ...pageIds])],
                            );
                          }}
                        />
                      </Th>
                    ) : null}
                    <Th
                      label="Name"
                      sortKey="full_name"
                      activeKey={sortBy}
                      direction={sortDir}
                      onSort={sortOn}
                    />
                    <Th label="Role" />
                    <Th label="Department" />
                    <Th
                      label="Status"
                      sortKey="status"
                      activeKey={sortBy}
                      direction={sortDir}
                      onSort={sortOn}
                    />
                    <Th
                      label="Last active"
                      sortKey="last_active_at"
                      activeKey={sortBy}
                      direction={sortDir}
                      onSort={sortOn}
                    />
                    <Th label="" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((user) => {
                    const isSelf = user.id === currentUser?.id;
                    return (
                      <Tr key={user.id} selected={selected.includes(user.id)}>
                        {canEdit ? (
                          <Td className={styles.checkCell}>
                            <Checkbox
                              checked={selected.includes(user.id)}
                              onChange={() => {
                                setSelected((current) =>
                                  current.includes(user.id)
                                    ? current.filter((id) => id !== user.id)
                                    : [...current, user.id],
                                );
                              }}
                              aria-label={`Select ${user.full_name}`}
                            />
                          </Td>
                        ) : null}

                        <Td>
                          <div className={styles.person}>
                            <Avatar name={user.full_name} src={photoUrlFor(user)} size={32} />
                            <div className={styles.personText}>
                              <span className={styles.personName}>
                                {user.full_name}
                                {isSelf ? <span className={styles.you}> · you</span> : null}
                              </span>
                              <span className={styles.personEmail}>{user.email}</span>
                            </div>
                          </div>
                        </Td>

                        <Td>{user.role.name}</Td>
                        <Td>
                          {user.department?.name ?? (
                            <span className={styles.none}>{NOTHING}</span>
                          )}
                        </Td>
                        <Td>
                          <Chip tone={STATUS_TONE[user.status]} dot>
                            {STATUS_LABEL[user.status]}
                          </Chip>
                        </Td>
                        <Td>
                          <span className={styles.when}>{lastActive(user.last_active_at)}</span>
                        </Td>

                        <Td className={styles.actionCell}>
                          <RowMenu
                            trigger={
                              <span className={styles.rowMenuButton}>
                                <MoreHorizontal size={17} />
                              </span>
                            }
                            label={`Actions for ${user.full_name}`}
                            items={[
                              ...(canEdit
                                ? [
                                    {
                                      label: 'Edit details',
                                      icon: <Pencil size={15} />,
                                      onSelect: () => {
                                        setDrawer({ open: true, user });
                                      },
                                    },
                                    {
                                      label: 'Send password reset',
                                      icon: <KeyRound size={15} />,
                                      onSelect: () => {
                                        issue('reset', user);
                                      },
                                    },
                                  ]
                                : []),
                              ...(canEdit && user.status === 'invited'
                                ? [
                                    {
                                      label: 'Resend invitation',
                                      icon: <Mail size={15} />,
                                      onSelect: () => {
                                        issue('invite', user);
                                      },
                                    },
                                  ]
                                : []),
                              ...(canEdit
                                ? [
                                    user.status === 'deactivated'
                                      ? {
                                          label: 'Activate account',
                                          icon: <UserCheck size={15} />,
                                          separated: true,
                                          onSelect: () => {
                                            setStatusFor.mutate(
                                              { id: user.id, status: 'active' },
                                              {
                                                onError: (error) => {
                                                  toast.error(
                                                    error instanceof ApiError
                                                      ? error.message
                                                      : 'That did not work.',
                                                  );
                                                },
                                              },
                                            );
                                          },
                                        }
                                      : {
                                          label: 'Deactivate account',
                                          icon: <UserX size={15} />,
                                          separated: true,
                                          disabled: isSelf,
                                          onSelect: () => {
                                            setStatusFor.mutate(
                                              { id: user.id, status: 'deactivated' },
                                              {
                                                // The chip is flipped optimistically
                                                // and put back on failure. Without a
                                                // message the whole refusal reads as
                                                // the row glitching, and the admin is
                                                // left believing the account was
                                                // deactivated when it was not.
                                                onError: (error) => {
                                                  toast.error(
                                                    error instanceof ApiError
                                                      ? error.message
                                                      : 'That did not work.',
                                                  );
                                                },
                                              },
                                            );
                                          },
                                        },
                                  ]
                                : []),
                              ...(canDelete
                                ? [
                                    {
                                      label: 'Remove',
                                      icon: <Trash2 size={15} />,
                                      danger: true,
                                      disabled: isSelf,
                                      onSelect: () => {
                                        setConfirm({ ids: [user.id], label: user.full_name });
                                      },
                                    },
                                  ]
                                : []),
                            ]}
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>

            <Pagination
              page={page}
              perPage={PER_PAGE}
              total={total}
              onChange={setPage}
              label="staff"
            />
          </>
        )}
      </Panel>

      <UserDrawer
        key={drawer.user?.id ?? 'new'}
        open={drawer.open}
        user={drawer.user}
        onClose={() => {
          setDrawer({ open: false, user: null });
        }}
        onCreated={(created) => {
          setLink({
            kind: 'invite',
            name: created.name,
            token: created.token,
            expiresAt: created.expiresAt,
          });
        }}
      />

      {link ? (
        <InviteLinkModal
          open
          kind={link.kind}
          personName={link.name}
          token={link.token}
          expiresAt={link.expiresAt}
          onClose={() => {
            setLink(null);
          }}
        />
      ) : null}

      <ConfirmModal
        open={confirm !== null}
        danger
        title={`Remove ${confirm?.label ?? ''}?`}
        body="The account loses access immediately and disappears from this list. Their record and history are kept, and they can be restored later. Deactivate instead if they may come back soon."
        confirmLabel="Remove"
        busy={bulk.isPending || removeUser.isPending}
        onCancel={() => {
          setConfirm(null);
        }}
        onConfirm={() => {
          if (!confirm) return;
          const [onlyId] = confirm.ids;
          if (confirm.ids.length === 1 && onlyId) {
            removeUser.mutate(onlyId, {
              onSuccess: () => {
                toast.success(`${confirm.label} removed.`);
                setSelected((current) => current.filter((id) => !confirm.ids.includes(id)));
              },
              onError: (error) => {
                toast.error(error instanceof ApiError ? error.message : 'That did not work.');
              },
              onSettled: () => {
                setConfirm(null);
              },
            });
            return;
          }
          runBulk('remove');
        }}
      />
    </Page>
  );
}
